import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { ContactShadows } from '@react-three/drei'
import * as THREE from 'three'
import type { LevelMeta } from '@/game/levels'
import type { BiomeTheme } from '@/styles/theme'
import type { BiomeId } from '@/styles/theme'
import type { EngineSnapshot } from '@/hooks/useGameEngine'
import type { SlideEvent, Vec } from '@/game/engine'
import { useBoardLayout, type BoardLayout } from './useBoardLayout'
import { Tiles } from './Tiles'
import { WorldEnvironment } from './WorldEnvironment'
import { Collectibles } from './Collectibles'
import { Ember } from './Ember'
import { Portal } from './Portal'
import { Particles, type BurstHandle } from './Particles'
import type { CharacterShape } from '@/game/characters'
import { boardGraphicsProfile, lowerGraphicsTier, motionCappedGraphicsTier, updateFallbackPressure, type GraphicsQuality } from './graphicsProfile'

interface GameBoard3DProps {
  level: LevelMeta
  snapshot: EngineSnapshot
  emberColor: string
  trailColor?: string | null
  theme: BiomeTheme
  reducedMotion: boolean
  graphics: GraphicsQuality
  events: { seq: number; list: SlideEvent[]; dir: [number, number] | null }
  shape?: CharacterShape
  equippedProps?: string[]
  bonkKey?: number
  characterId?: string
}

interface SceneProps extends GameBoard3DProps {
  onPerformanceFallback: () => void
}

export interface BoardLightingProfile {
  ambientColor: string
  ambientIntensity: number
  hemisphereSky: string
  hemisphereGround: string
  hemisphereIntensity: number
  keyColor: string
  keyPosition: [number, number, number]
  keyIntensity: number
  fillColor: string
  fillPosition: [number, number, number]
  fillIntensity: number
  fogColor: string
  fogNearScale: number
  fogFarScale: number
}

export function boardCameraFrame(layout: BoardLayout, size: { width: number; height: number }) {
  const pitch = layout.tileSize + layout.gap
  const baseDist = Math.max(7.8, layout.extent * 0.86)
  const baseHeight = Math.max(7.7, layout.extent * 0.92)
  const baseRange = Math.hypot(baseDist, baseHeight)
  const aspect = size.width / Math.max(1, size.height)
  const vHalf = Math.tan((42 * Math.PI) / 360)
  const boardHalfW = (layout.cols * pitch) / 2
  const boardHalfD = (layout.rows * pitch) / 2
  const halfW = boardHalfW * 1.72 + 1.65
  const halfD = boardHalfD * 1.05 + 1.85
  const fit = Math.max(1, halfW / (vHalf * aspect * baseRange), halfD / (vHalf * baseRange))
  const portrait = aspect < 0.85 ? 1 + (0.85 - aspect) * 0.78 : 1
  const follow = aspect < 0.85 ? 0.12 : 0.16
  return { frameDist: baseDist * fit * portrait, frameHeight: baseHeight * fit * portrait, camRange: baseRange * fit * portrait, follow }
}

export function boardLightingProfile(theme: BiomeTheme, graphics: GraphicsQuality): BoardLightingProfile {
  const fillScale = graphics === 'low' ? 0 : 1
  const base: Record<BiomeId, Omit<BoardLightingProfile, 'fillIntensity'>> = {
    forest: {
      ambientColor: '#bfeea8',
      ambientIntensity: 0.58,
      hemisphereSky: '#d8ffbc',
      hemisphereGround: '#102015',
      hemisphereIntensity: 0.8,
      keyColor: '#fff0c2',
      keyPosition: [3.8, 9.4, 4.6],
      keyIntensity: 1.62,
      fillColor: '#6be08f',
      fillPosition: [-4.8, 2.8, -3.8],
      fogColor: '#0c1712',
      fogNearScale: 0.52,
      fogFarScale: 2.38,
    },
    cave: {
      ambientColor: '#b8abff',
      ambientIntensity: 0.46,
      hemisphereSky: '#c0b6ff',
      hemisphereGround: '#0a0914',
      hemisphereIntensity: 0.7,
      keyColor: '#d5ceff',
      keyPosition: [-4.8, 9.2, 4.2],
      keyIntensity: 1.34,
      fillColor: '#68e3ff',
      fillPosition: [4.8, 2.8, -4.2],
      fogColor: '#0d0c16',
      fogNearScale: 0.42,
      fogFarScale: 2.16,
    },
    lava: {
      ambientColor: '#ff6a45',
      ambientIntensity: 0.38,
      hemisphereSky: '#ff9a7a',
      hemisphereGround: '#140604',
      hemisphereIntensity: 0.62,
      keyColor: '#ffd0a0',
      keyPosition: [4.2, 8.8, 3.8],
      keyIntensity: 1.24,
      fillColor: '#ff3d16',
      fillPosition: [-4.8, 2.4, -3.4],
      fogColor: '#160a08',
      fogNearScale: 0.36,
      fogFarScale: 2.0,
    },
    ice: {
      ambientColor: '#c9f8ff',
      ambientIntensity: 0.6,
      hemisphereSky: '#dcfbff',
      hemisphereGround: '#071118',
      hemisphereIntensity: 0.86,
      keyColor: '#f4fbff',
      keyPosition: [-4.4, 9.8, 4.8],
      keyIntensity: 1.48,
      fillColor: '#5fd6ff',
      fillPosition: [4.8, 2.8, -4.2],
      fogColor: '#081218',
      fogNearScale: 0.5,
      fogFarScale: 2.28,
    },
    magical: {
      ambientColor: '#f2a7ff',
      ambientIntensity: 0.5,
      hemisphereSky: '#ffc2ff',
      hemisphereGround: '#100716',
      hemisphereIntensity: 0.74,
      keyColor: '#fbd1ff',
      keyPosition: [-3.8, 9.1, 4.4],
      keyIntensity: 1.36,
      fillColor: '#7de3ff',
      fillPosition: [4.6, 3.0, -4.6],
      fogColor: '#130a18',
      fogNearScale: 0.4,
      fogFarScale: 2.18,
    },
    dungeon: {
      ambientColor: '#e8c47f',
      ambientIntensity: 0.42,
      hemisphereSky: '#f0ca83',
      hemisphereGround: '#0d0905',
      hemisphereIntensity: 0.68,
      keyColor: '#ffd89a',
      keyPosition: [4.8, 8.7, 4.1],
      keyIntensity: 1.42,
      fillColor: '#ffb454',
      fillPosition: [-4.2, 2.5, -3.6],
      fogColor: '#100d09',
      fogNearScale: 0.34,
      fogFarScale: 2.06,
    },
  }
  const selected = base[theme.id]
  const fillIntensity = (
    theme.id === 'lava' ? 1.12 :
      theme.id === 'magical' ? 0.96 :
        theme.id === 'ice' ? 0.78 :
          theme.id === 'cave' ? 0.72 :
            theme.id === 'dungeon' ? 0.82 : 0.52
  ) * fillScale
  return { ...selected, fillIntensity }
}

function GameBoard3DView(props: GameBoard3DProps) {
  const { graphics, reducedMotion } = props
  const [effectiveGraphics, setEffectiveGraphics] = useState<GraphicsQuality>(graphics)
  const [motionCostCap, setMotionCostCap] = useState(false)
  useEffect(() => {
    setEffectiveGraphics(graphics)
    setMotionCostCap(false)
  }, [graphics, props.level.index])
  useEffect(() => {
    if (props.snapshot.phase === 'sliding') setMotionCostCap(true)
  }, [props.snapshot.phase])
  const renderGraphics = motionCappedGraphicsTier(effectiveGraphics, motionCostCap || props.snapshot.phase === 'sliding')
  const profile = boardGraphicsProfile(renderGraphics, props.snapshot.phase)
  const sceneProps = useMemo(() => ({ ...props, graphics: renderGraphics }), [props, renderGraphics])
  return (
    <Canvas
      frameloop={profile.frameloop}
      shadows={profile.shadows}
      dpr={profile.dpr}
      camera={{ position: [0, 7.5, 7], fov: 42, near: 0.1, far: 100 }}
      gl={{ antialias: profile.antialias, alpha: true, powerPreference: 'high-performance' }}
    >
      <Scene {...sceneProps} reducedMotion={reducedMotion} onPerformanceFallback={() => setEffectiveGraphics((current) => lowerGraphicsTier(current))} />
    </Canvas>
  )
}

export const GameBoard3D = memo(GameBoard3DView)

function Scene({ level, snapshot, emberColor, trailColor, theme, reducedMotion, graphics, events, shape, equippedProps, bonkKey, characterId, onPerformanceFallback }: SceneProps) {
  const layout = useBoardLayout(level)
  const burstRef = useRef<BurstHandle>(null)
  const { camera, scene, size, invalidate } = useThree()
  const profile = useMemo(() => boardGraphicsProfile(graphics, snapshot.phase), [graphics, snapshot.phase])
  const lighting = useMemo(() => boardLightingProfile(theme, graphics), [graphics, theme])

  const { frameDist, frameHeight, camRange, follow } = useMemo(() => boardCameraFrame(layout, size), [layout, size])

  const startPos = useMemo(() => {
    for (let y = 0; y < level.grid.length; y++) {
      const x = level.grid[y].indexOf('o')
      if (x >= 0) return { x, y }
    }
    return { x: 0, y: 0 }
  }, [level.grid])

  const homePos = useMemo(() => {
    for (let y = 0; y < level.grid.length; y++) {
      const x = level.grid[y].indexOf('e')
      if (x >= 0) return { x, y }
    }
    return { x: 0, y: 0 }
  }, [level.grid])

  useEffect(() => {
    scene.fog = new THREE.Fog(new THREE.Color(lighting.fogColor), camRange + layout.extent * lighting.fogNearScale, camRange + layout.extent * lighting.fogFarScale)
    return () => {
      scene.fog = null
    }
  }, [scene, lighting.fogColor, lighting.fogNearScale, lighting.fogFarScale, layout.extent, camRange])

  useEffect(() => {
    camera.position.set(0, frameHeight, frameDist)
    camera.lookAt(0, -0.34, 0)
  }, [camera, frameHeight, frameDist])

  useEffect(() => {
    if (profile.frameloop === 'demand') invalidate()
  }, [invalidate, level.index, profile.frameloop])

  const emberRef = useRef<THREE.Group>(null)
  const pathRef = useRef<Vec[]>(snapshot.path)
  const slideProgress = useRef(1)
  const slideDir = useRef<[number, number] | null>(null)
  const shake = useRef(0)
  const zoomPulse = useRef(0)
  const lastSeq = useRef(-1)
  const trailAccum = useRef(0)
  const slowFrameStreak = useRef(0)
  const activeMotionRef = useRef(false)
  const postMoveAnimationLeft = useRef(0)

  useEffect(() => {
    slowFrameStreak.current = 0
    activeMotionRef.current = false
    postMoveAnimationLeft.current = 0
  }, [graphics, level.index])

  useEffect(() => {
    if (!reducedMotion && profile.postMoveAnimationSeconds > 0 && (snapshot.phase === 'sliding' || snapshot.phase === 'idle')) {
      postMoveAnimationLeft.current = Math.max(postMoveAnimationLeft.current, profile.postMoveAnimationSeconds)
    }
    if (profile.frameloop === 'demand') invalidate()
  }, [invalidate, level.index, profile.frameloop, profile.postMoveAnimationSeconds, reducedMotion, snapshot.phase, snapshot.pos.x, snapshot.pos.y, events.seq])

  useEffect(() => {
    if (events.seq === lastSeq.current) return
    lastSeq.current = events.seq
    for (const ev of events.list) {
      if (!burstRef.current) break
      const at = worldOf(ev)
      if (ev.kind === 'coin') burstRef.current.burst([at[0], 0.5, at[2]], '#ffcf5c', 14, 1.4, 1.6)
      else if (ev.kind === 'gem') burstRef.current.burst([at[0], 0.5, at[2]], '#7de3ff', 20, 1.6, 2)
      else if (ev.kind === 'key' || ev.kind === 'unlock') burstRef.current.burst([at[0], 0.5, at[2]], '#ffe08a', 16, 1.2, 1.5)
      else if (ev.kind === 'shatter') { burstRef.current.burst([at[0], 0.5, at[2]], theme.biomeSoft, 24, 1.8, 2); if (!reducedMotion) shake.current = 0.5 }
      else if (ev.kind === 'impact') { burstRef.current.burst([at[0], 0.35, at[2]], theme.biomeSoft, 10, 1, 0.8); if (!reducedMotion) shake.current = 0.6 }
      else if (ev.kind === 'hazard') { burstRef.current.burst([at[0], 0.5, at[2]], '#ff6a5c', 30, 2, 2.4); if (!reducedMotion) shake.current = 1 }
      else if (ev.kind === 'home') { burstRef.current.burst([at[0], 0.6, at[2]], emberColor, 60, 2.4, 3); if (!reducedMotion) zoomPulse.current = 1 }
    }
    function worldOf(ev: SlideEvent): [number, number, number] {
      const p = 'x' in ev ? { x: ev.x, y: ev.y } : ev.kind === 'teleport' ? ev.to : { x: 0, y: 0 }
      return layout.toWorld(p.x, p.y)
    }
  }, [events.seq, events.list, layout, theme.biomeSoft, emberColor, reducedMotion])

  useEffect(() => {
    if (snapshot.phase === 'sliding') {
      pathRef.current = snapshot.path
      slideProgress.current = 0
      slideDir.current = events.dir
    }
  }, [snapshot.phase, snapshot.path, events.dir])

  useFrame((state, delta) => {
    const g = emberRef.current
    if (!g) return
    const path = pathRef.current
    const sliding = snapshot.phase === 'sliding'
    const activeMotion = sliding || slideProgress.current < 1 || shake.current > 0.001 || zoomPulse.current > 0.001 || (events.seq > 0 && postMoveAnimationLeft.current > 0)
    if (graphics !== 'low' && activeMotion) {
      const measuredDelta = !activeMotionRef.current && delta > 1 ? profile.performanceFallbackDelta : delta
      activeMotionRef.current = true
      const pressure = updateFallbackPressure(slowFrameStreak.current, measuredDelta, profile)
      slowFrameStreak.current = pressure.pressure
      if (pressure.fallback) {
        slowFrameStreak.current = 0
        onPerformanceFallback()
      }
    } else {
      activeMotionRef.current = false
      slowFrameStreak.current = Math.max(0, slowFrameStreak.current - 0.2)
    }
    let needsFrame = sliding || slideProgress.current < 1 || shake.current > 0.001 || zoomPulse.current > 0.001 || (!reducedMotion && postMoveAnimationLeft.current > 0)

    if (sliding && path.length > 1) {
      const speed = reducedMotion ? 6 : (snapshot.slowmo ? 3 : 9)
      slideProgress.current = Math.min(1, slideProgress.current + delta * speed)
      const total = path.length - 1
      const f = slideProgress.current * total
      const i = Math.min(total - 1, Math.floor(f))
      const localT = f - i
      const a = path[i]
      const b = path[i + 1]
      const [ax, , az] = layout.toWorld(a.x, a.y)
      const [bx, , bz] = layout.toWorld(b.x, b.y)
      g.position.x = ax + (bx - ax) * localT
      g.position.z = az + (bz - az) * localT

      if (profile.trailParticles && trailColor && !reducedMotion && burstRef.current) {
        trailAccum.current += delta
        const interval = 0.022
        while (trailAccum.current >= interval) {
          trailAccum.current -= interval
          burstRef.current.burst([g.position.x, 0.42, g.position.z], trailColor, 1, 0.12, 0.18)
        }
      }
    } else {
      trailAccum.current = 0
      if (postMoveAnimationLeft.current > 0) postMoveAnimationLeft.current = Math.max(0, postMoveAnimationLeft.current - delta)
      const target = snapshot.pos
      const [tx, , tz] = layout.toWorld(target.x, target.y)
      needsFrame = needsFrame || Math.abs(tx - g.position.x) > 0.01 || Math.abs(tz - g.position.z) > 0.01
      g.position.x += (tx - g.position.x) * Math.min(1, delta * 12)
      g.position.z += (tz - g.position.z) * Math.min(1, delta * 12)
    }

    if (zoomPulse.current > 0.001) zoomPulse.current = Math.max(0, zoomPulse.current - delta * 0.9)
    const breathe = reducedMotion ? 0 : Math.sin(state.clock.elapsedTime * 0.22) * 0.035
    const slowPush = sliding && !reducedMotion ? -0.035 : 0
    const zoomFactor = 1 + breathe + slowPush - zoomPulse.current * 0.16
    const dist = frameDist * zoomFactor
    const camTargetX = g.position.x * follow
    const camTargetZ = g.position.z * follow + dist
    camera.position.x += (camTargetX - camera.position.x) * Math.min(1, delta * 3)
    camera.position.z += (camTargetZ - camera.position.z) * Math.min(1, delta * 3)

    let sx = 0
    let sy = 0
    if (shake.current > 0.001) {
      shake.current = Math.max(0, shake.current - delta * 3)
      const amp = shake.current * 0.25
      sx = (Math.random() - 0.5) * amp
      sy = (Math.random() - 0.5) * amp
    }
    camera.position.x += sx
    camera.position.y = frameHeight * zoomFactor + sy
    camera.lookAt(g.position.x * follow, -0.34, g.position.z * follow)
    if (!reducedMotion) {
      camera.position.y += Math.sin(state.clock.elapsedTime * 0.6) * 0.05
    }
    if (profile.frameloop === 'demand' && needsFrame) invalidate()
  })

  const startWorld = layout.toWorld(startPos.x, startPos.y)
  const homeWorld = layout.toWorld(homePos.x, homePos.y)

  return (
    <>
      <ambientLight intensity={lighting.ambientIntensity} color={lighting.ambientColor} />
      <hemisphereLight args={[lighting.hemisphereSky, lighting.hemisphereGround, lighting.hemisphereIntensity]} />
      <directionalLight
        position={lighting.keyPosition}
        intensity={lighting.keyIntensity}
        color={lighting.keyColor}
        castShadow={profile.shadows}
        shadow-mapSize-width={profile.shadowMapSize}
        shadow-mapSize-height={profile.shadowMapSize}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
      {lighting.fillIntensity > 0 && profile.worldPointLightIntensity > 0 && (
        <pointLight position={lighting.fillPosition} intensity={lighting.fillIntensity * profile.worldPointLightIntensity} color={lighting.fillColor} />
      )}

      <WorldEnvironment layout={layout} theme={theme} biome={level.biome} reducedMotion={reducedMotion} graphics={graphics} emberColor={emberColor} grid={level.grid} />
      <Tiles cells={snapshot.cells} layout={layout} theme={theme} reducedMotion={reducedMotion} graphics={graphics} />
      <Collectibles cells={snapshot.cells} layout={layout} reducedMotion={reducedMotion} graphics={graphics} />

      <Portal position={[startWorld[0], 0, startWorld[2]]} color={theme.biomeSoft} variant="start" reducedMotion={reducedMotion} graphics={graphics} />
      <Portal position={[homeWorld[0], 0, homeWorld[2]]} color={emberColor} variant="home" reducedMotion={reducedMotion} graphics={graphics} />

      <group ref={emberRef} position={layout.toWorld(startPos.x, startPos.y)}>
        <Ember
          color={emberColor}
          phase={snapshot.phase}
          slideDir={slideDir.current}
          facing={snapshot.facing}
          reducedMotion={reducedMotion}
          shape={shape}
          bonkKey={bonkKey}
          characterId={characterId}
          equippedProps={equippedProps}
          graphics={graphics}
        />
      </group>

      {profile.contactShadows && (
        <ContactShadows position={[0, 0.25, 0]} opacity={0.46} scale={layout.extent * 1.5} blur={2} far={3.4} resolution={profile.contactShadowResolution} frames={1} color={theme.ink} />
      )}

      <Particles ref={burstRef} reducedMotion={reducedMotion} graphics={graphics} />
    </>
  )
}
